import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Send, MessageCircle, Check, CheckCheck, User, Store } from 'lucide-react';
import { ChatService, type MensajeChat } from '../../services/chat/chat.service';
import { webSocketService } from '../../services/websocket/websocket.service';
import { formatDistanceToNow } from '../../utils/date';

interface OrderChatProps {
  pedidoId: string;
  numeroPedido: string;
  clienteEmail?: string;
  clienteNombre?: string;
  isAdmin?: boolean; // true = vista admin, false = vista cliente
  className?: string;
}

export const OrderChat: React.FC<OrderChatProps> = ({
  pedidoId,
  numeroPedido,
  clienteEmail,
  clienteNombre,
  isAdmin = false,
  className = '',
}) => {
  const [mensajes, setMensajes] = useState<MensajeChat[]>([]);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Desduplicar mensajes por ID antes de renderizar
  const mensajesUnicos = useMemo(() => {
    const seen = new Set<string>();
    return mensajes.filter(m => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [mensajes]);

  // Cargar mensajes iniciales
  useEffect(() => {
    cargarMensajes();
    // Marcar como leídos al abrir
    marcarLeidos();
  }, [pedidoId]);

  // Scroll al último mensaje
  useEffect(() => {
    scrollToBottom();
  }, [mensajesUnicos]);

  // Enfocar input después de enviar mensaje
  useEffect(() => {
    if (!enviando && !nuevoMensaje) {
      // Pequeño delay para asegurar que el input ya esté habilitado
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [enviando, nuevoMensaje]);

  // Escuchar mensajes en tiempo real vía WebSocket
  useEffect(() => {
    // Conectar WebSocket si no está conectado
    if (!webSocketService.isConnected()) {
      webSocketService.connect().catch(() => {
        // Silenciar errores
      });
    }
    
    const handleChatMessage = (payload: any) => {
      if (payload.pedidoId === pedidoId) {
        setMensajes((prev) => {
          // Evitar duplicados
          const exists = prev.find((m) => m.id === payload.mensaje.id);
          if (exists) return prev;
          return [...prev, payload.mensaje];
        });
        
        // Marcar como leído automáticamente si estamos en el chat
        if (!isAdmin || document.visibilityState === 'visible') {
          marcarLeidos();
        }
      }
    };

    // Suscribirse a mensajes específicos de chat
    (webSocketService as any).addEventListener('order_chat_message', handleChatMessage);
    
    return () => {
      (webSocketService as any).removeEventListener('order_chat_message', handleChatMessage);
    };
  }, [pedidoId, isAdmin]);

  const cargarMensajes = async () => {
    setLoading(true);
    setError(null);
    
    const response = isAdmin
      ? await ChatService.getMensajes(pedidoId)
      : await ChatService.getMensajesCliente(pedidoId);

    if (response.success) {
      setMensajes(response.data);
    } else {
      setError(response.message || 'Error al cargar mensajes');
    }
    setLoading(false);
  };

  const marcarLeidos = async () => {
    if (isAdmin) {
      await ChatService.marcarLeidos(pedidoId);
    } else {
      await ChatService.marcarLeidosCliente(pedidoId);
    }
  };

  const enviarMensaje = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!nuevoMensaje.trim()) return;

    setEnviando(true);
    setError(null);

    const requestData = isAdmin
      ? { mensaje: nuevoMensaje.trim(), tipo: 'TEXTO' }
      : {
          mensaje: nuevoMensaje.trim(),
          tipo: 'TEXTO',
          nombreRemitente: clienteNombre || 'Cliente',
          emailRemitente: clienteEmail,
        };

    const response = isAdmin
      ? await ChatService.enviarMensaje(pedidoId, requestData)
      : await ChatService.enviarMensajeCliente(pedidoId, requestData);

    if (response.success && response.data) {
      setMensajes((prev) => [...prev, response.data!]);
      setNuevoMensaje('');
    } else {
      setError(response.message || 'Error al enviar mensaje');
    }

    setEnviando(false);
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const getRemitenteIcon = (remitente: string) => {
    switch (remitente) {
      case 'ADMIN':
        return <Store className="w-4 h-4" />;
      case 'CLIENTE':
        return <User className="w-4 h-4" />;
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getRemitenteLabel = (remitente: string, nombre: string) => {
    switch (remitente) {
      case 'ADMIN':
        return 'Farmacia';
      case 'SISTEMA':
        return 'Sistema';
      default:
        return nombre;
    }
  };

  const getRemitenteColor = (remitente: string) => {
    switch (remitente) {
      case 'ADMIN':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'SISTEMA':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  return (
    <div className={`flex flex-col h-full rounded-lg border bg-gray-100 border-gray-300 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-2 bg-white border-b border-gray-300 flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-emerald-600" />
          <div>
            <h3 className="font-semibold text-sm text-gray-900">Chat</h3>
            <p className="text-xs text-gray-500 font-medium">{numeroPedido}</p>
          </div>
        </div>
        <button
          onClick={cargarMensajes}
          className="text-xs text-emerald-600 hover:text-emerald-700"
          disabled={loading}
        >
          {loading ? '...' : '↻'}
        </button>
      </div>

      {/* Mensajes */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-100 min-h-0">
        {mensajesUnicos.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No hay mensajes aún</p>
            <p className="text-xs mt-1">Sé el primero en escribir</p>
          </div>
        ) : (
          mensajesUnicos.map((mensaje, index) => {
            const esMio = isAdmin
              ? mensaje.remitente === 'ADMIN'
              : mensaje.remitente === 'CLIENTE';
            const showAvatar = index === 0 || mensajesUnicos[index - 1].remitente !== mensaje.remitente;

            return (
              <div
                key={mensaje.id}
                className={`flex ${esMio ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex max-w-[80%] ${esMio ? 'flex-row-reverse' : 'flex-row'} gap-2`}>
                  {/* Avatar */}
                  {showAvatar && (
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getRemitenteColor(mensaje.remitente)}`}>
                      {getRemitenteIcon(mensaje.remitente)}
                    </div>
                  )}
                  {!showAvatar && <div className="w-8" />}

                  {/* Burbuja */}
                  <div className="flex flex-col">
                    {showAvatar && (
                      <span className={`text-xs text-gray-600 mb-1 ${esMio ? 'text-right' : 'text-left'}`}>
                        {getRemitenteLabel(mensaje.remitente, mensaje.nombreRemitente)}
                      </span>
                    )}
                    <div
                      className={`px-3 py-2 rounded-2xl text-sm ${
                        esMio
                          ? 'bg-emerald-500 text-white rounded-br-md'
                          : mensaje.remitente === 'SISTEMA'
                          ? 'bg-white text-gray-600 border border-gray-300 rounded-bl-md'
                          : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'
                      }`}
                    >
                      {mensaje.mensaje}
                    </div>
                    <div className={`flex items-center gap-1 mt-1 ${esMio ? 'justify-end' : 'justify-start'}`}>
                      <span className="text-[10px] text-gray-500">
                        {formatDistanceToNow(mensaje.creadoEn)}
                      </span>
                      {esMio && (
                        mensaje.leido ? (
                          <CheckCheck className="w-3 h-3 text-emerald-500" />
                        ) : (
                          <Check className="w-3 h-3 text-gray-300" />
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-red-50 text-red-600 text-sm text-center">
          {error}
        </div>
      )}

      {/* Input */}
      <form onSubmit={enviarMensaje} className="flex-shrink-0 px-3 pt-2 pb-1 bg-white border-t border-gray-300">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={nuevoMensaje}
            onChange={(e) => setNuevoMensaje(e.target.value)}
            placeholder="Escribe tu mensaje..."
            className="flex-1 px-4 py-2.5 rounded-full border border-gray-300 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-sm bg-white outline-none"
            disabled={enviando}
          />
          <button
            type="submit"
            disabled={!nuevoMensaje.trim() || enviando}
            className="px-4 py-2.5 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            {enviando ? (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
